import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { LoaderCircle, MessagePopup } from '@skytech/ui'
import { StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { NameAvatar } from '@skytech/ui/NameAvatar'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LoadingError } from '@/components/LoadingError'
import Tabs from '@/components/Tabs'
import { useIsAdmin } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import type { CostObjectDetails as CostObjectDetailsType } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { Assets } from './Assets'
import { EmployeeConsentIndicator } from './ConsentIndicator'
import PolicySubscriptions from './PolicySubscriptions'
import Profile from './Profile'

import './index.styles.scss'

export const CostObjectDetails: FC = () => {
  const isAdmin = useIsAdmin()

  const navigate = useNavigate()
  const translate = useTranslation()
  const params = useParams()
  const { whiteLabel } = useAppContext()

  const costObjectId = Number(params.costObjectId)

  if (isNaN(costObjectId)) throw Error(translate('Employee ID is not a number!'))

  const { data, isError } = useQuery(queries.adminCostObjectDetails(costObjectId), () =>
    adminResources.getCostObjectDetails(costObjectId)
  )

  const { data: assetsData, isLoading: isAssetsLoading } = useQuery(
    queries.adminAssetList({ allAssets: true }),
    () => adminResources.getAssetListWithFilters({ allAssets: true })
  )
  const employeeAssets = assetsData?.assets.filter(asset => asset.costObjectId === costObjectId)

  const costObjectDetailsTabs = useMemo(() => {
    const tabs = []
    if (isAdmin) {
      tabs.push({
        value: 'profile',
        label: <Translation id="Profile" />,
      })
    }

    tabs.push({
      value: 'policy-subs',
      label: (
        <>
          <Translation id="Policy subscriptions" />
          {'  '}
          {data?.ecomPolicySubscriptions?.length === 0 ? (
            <StyledText size="regular-plus" colorMode="gray" className="count-styling">
              {data?.ecomPolicySubscriptions?.length}
            </StyledText>
          ) : (
            <StyledText size="regular-plus" className="count-styling">
              {data?.ecomPolicySubscriptions?.length}
            </StyledText>
          )}
        </>
      ),
    })

    tabs.push({
      value: 'assets',
      label: (
        <>
          {<Translation id="Assets" />}{' '}
          {isAssetsLoading ? (
            <Translation id="Loading..." />
          ) : (
            <>
              {employeeAssets?.length === 0 ? (
                <StyledText size="regular-plus" colorMode="gray" className="count-styling">
                  {employeeAssets?.length}
                </StyledText>
              ) : (
                <StyledText size="regular-plus" className="count-styling">
                  {employeeAssets?.length}
                </StyledText>
              )}
            </>
          )}
        </>
      ),
    })

    return tabs
  }, [data?.ecomPolicySubscriptions?.length, employeeAssets?.length, isAdmin, isAssetsLoading])

  const [queryTab, setQueryTab] = useSearchParamsByKey('tab')
  const [tabFilter, setTabFilter] = useState(queryTab || costObjectDetailsTabs[0].value)

  useEffect(() => {
    setTabFilter(queryTab || costObjectDetailsTabs[0].value)
  }, [costObjectDetailsTabs, queryTab])

  if (isError)
    return (
      <Container onSwipedLeft={() => navigate(APP_ROUTES.ADMIN_COST_OBJECTS)}>
        <LoadingError />
      </Container>
    )

  if (!data) {
    return (
      <Container onSwipedLeft={() => navigate(APP_ROUTES.ADMIN_COST_OBJECTS)}>
        <LoaderCircle />
      </Container>
    )
  }

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.ADMIN_COST_OBJECTS)}>
      <div className="mobile:flex-row mobile:gap-3 flex flex-col items-start gap-2">
        <NameAvatar
          className="mb-8 self-center"
          id={data.id}
          name={data.name}
          status={
            data.onOffBoarding && data.onOffBoardingStatus
              ? {
                  onOffBoarding: data.onOffBoarding,
                  onOffBoardingStatus: data.onOffBoardingStatus,
                }
              : undefined
          }
          whiteLabel={whiteLabel}
          translate={translate}
        />
        <div className="employee-details-title">
          <div className="flex items-center">
            <Container.Header>{data.name}</Container.Header>{' '}
            {data.type === 'E' && (
              <span className="mx-1">
                (<Translation id="Private number" />)
              </span>
            )}
            {data?.consent && <ConsentIndicator costObject={data} />}
          </div>
          <h4 className="customer-name">{data.customerName}</h4>
        </div>
      </div>

      <Tabs
        selectedValue={tabFilter}
        tabs={costObjectDetailsTabs}
        onClick={tabId => {
          setTabFilter(String(tabId))
          setQueryTab(String(tabId))
        }}
      />

      <div className="cost-object-details">
        {tabFilter === 'profile' && (
          <Profile
            costObjectDetails={data}
            onGoToAssetTab={() => {
              setTabFilter('assets')
              setQueryTab('assets')
            }}
          />
        )}
        {tabFilter === 'policy-subs' && <PolicySubscriptions />}
        {tabFilter === 'assets' && <Assets />}
      </div>
    </Container>
  )
}

const ConsentIndicator: FC<{ costObject: CostObjectDetailsType }> = ({ costObject }) => {
  const queryClient = useQueryClient()
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  const revokeConsentMutation = useMutation(adminResources.revokeConsent, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminCostObjectDetails(costObject.id))
      queryClient.invalidateQueries(queries.adminConsents())
      setIsPopupOpen(false)
    },
  })

  const isConsentActive = Boolean(
    costObject.consent?.consentGivenDate && !costObject.consent?.isRevoked
  )

  return (
    <>
      <Button
        className={twJoin('m-0 p-0', !isConsentActive ? 'cursor-default' : 'cursor-pointer')}
        id="consent-indicator"
        variant="tertiary"
        onClick={() => isConsentActive && setIsPopupOpen(true)}
      >
        <EmployeeConsentIndicator consent={costObject.consent} />
      </Button>

      <MessagePopup
        variant="narrow"
        type="success"
        title={<Translation id="Employee consent" />}
        message={
          <Translation id="Removing consent forces user to agree to the given terms on their next login" />
        }
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
      >
        <footer className="mt-2 flex items-center justify-end">
          <Button className="mb-0" variant="tertiary" onClick={() => setIsPopupOpen(false)}>
            <Translation id="Cancel" />
          </Button>

          <Button
            className="mb-0"
            isLoading={revokeConsentMutation.isLoading}
            onClick={() => {
              if (!costObject.consent) throw Error('No consent found!')
              if (!isConsentActive) throw Error('Consent is not active!')
              revokeConsentMutation.mutate({
                consentId: costObject.consent.id,
                costObjectId: costObject.id,
              })
            }}
          >
            <Translation id="Revoke consent" />
          </Button>
        </footer>
      </MessagePopup>
    </>
  )
}
